import React from 'react';
import { generatePath } from 'react-router-dom';
import { useMedia, useTitle } from 'react-use';

import { BUTTON_TYPES, media } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { Heading } from '@savgroup-front-common/core/atoms/Heading';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import {
  buildPageTitle,
  SUPPORTED_ICON_POSITIONS,
} from '@savgroup-front-common/core/helpers';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import ArrowNakedLeftIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedLeft.icon';
import { ArrowNakedRightIcon } from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedRight.icon';
import { MyAccountIcon } from '@savgroup-front-common/core/protons/IconsNewDesign/MyAccount.icon';
import { ICONS_TYPE } from '@savgroup-front-common/types/Icon';

import { ResumeCard } from '@/view/components/ResumeCard';
import { ResumeCardButton } from '@/view/components/ResumeCardButton';

import { FINAL_ROUTES } from '../../../app';
import { STEP_CONFIG } from '../../../app/NewLayout/ClassiqueRoutes.config';
import {
  NewStepLayout,
  PageFooterContainer,
} from '../../../app/NewLayout/NewStepLayout';
import { ResumeDeposit } from '../../../components/ResumeDeposit';
import { ResumeSolution } from '../../../components/ResumeSolution';
import { AvailableHandlingMethods } from '../components/AvailableHandlingMethods/AvailableHandlingMethods';
import { IrshStepValues } from '../IrshPages.types';

import useClaimGroupDeliveryPage from './ClaimGroupDeliveryPage.hooks';
import {
  $DeliveryItemWrapper,
  $DeliveryResultWrapper,
  $DeliveryWrapper,
  $HeadingIcon,
  $LeftColumn,
} from './ClaimGroupDeliveryPage.styles';
import messages from './messages';

export const ClaimGroupDeliveryPage: React.FC<
  RoutedStepProps<IrshStepValues>
> = ({ onNextStep, onPreviousStep }) => {
  useTitle(buildPageTitle([messages.claimGroupDeliveryTitlePage]));

  const isMobileView = useMedia(media.maxWidth.xs);

  const {
    claimGroupConfirmation,
    claimGroupId,
    handling,
    onSubmit,
    handleSetPickupPoint,
    handleSetAddress,
    deliveryAvailableMethods,
    handlingValues,
    isSubmitLoading,
    isLoading,
    pickupPointSuggested,
    isGetTransportPlansLoading,
  } = useClaimGroupDeliveryPage({
    onNextStep,
  });

  const isValid =
    (!!handlingValues?.address || !!handlingValues?.pickupPointSelected) &&
    !!handlingValues?.selectedMethod;

  if (
    !handling ||
    !claimGroupConfirmation ||
    isLoading ||
    isGetTransportPlansLoading
  ) {
    return <BaseLoader />;
  }

  return (
    <>
      <NewStepLayout stepName={STEP_CONFIG.CLAIM_GROUP_DELIVERY}>
        <$DeliveryWrapper>
          <$LeftColumn>
            <ResumeSolution
              claimGroupId={claimGroupId}
              claimGroupConfirmation={claimGroupConfirmation}
              customBuildUpdateSolutionUrl={generatePath(
                FINAL_ROUTES.CLAIM_GROUP_SOLUTION,
                {
                  claimGroupId,
                },
              )}
            />
            {handling?.hasDeposit && (
              <ResumeDeposit handlingSummary={handling} />
            )}
            <Heading level={3} dataTestId="handling-delivery-title">
              <$HeadingIcon>
                <MyAccountIcon icon={ICONS_TYPE.DELIVERY_ICON} />
              </$HeadingIcon>
              <SafeFormattedMessageWithoutSpread
                message={
                  handling?.hasHome
                    ? messages.handlingTitleOnlyHome
                    : messages.handlingTitle
                }
              />
            </Heading>
            <$DeliveryItemWrapper>
              <AvailableHandlingMethods
                handlingSummary={handling}
                availableMethods={deliveryAvailableMethods}
                onSetPickupPoint={handleSetPickupPoint}
                onSetAddress={handleSetAddress}
                handlingValues={handlingValues}
                pickupPointSuggested={pickupPointSuggested}
              />
            </$DeliveryItemWrapper>
          </$LeftColumn>

          <$DeliveryResultWrapper>
            {isMobileView && (
              <ResumeCardButton
                claimGroupConfirmation={claimGroupConfirmation}
                isTransportPlanFeatureEnabled
              />
            )}
            {!isMobileView && (
              <ResumeCard
                claimGroupConfirmation={claimGroupConfirmation}
                customLinkModify={generatePath(
                  FINAL_ROUTES.CLAIM_GROUP_REASON,
                  {
                    claimGroupId,
                  },
                )}
                isTransportPlanFeatureEnabled
              />
            )}
          </$DeliveryResultWrapper>
        </$DeliveryWrapper>
      </NewStepLayout>
      <PageFooterContainer>
        <Button
          primary
          type={BUTTON_TYPES.BUTTON}
          icon={<ArrowNakedRightIcon />}
          position={SUPPORTED_ICON_POSITIONS.RIGHT}
          disabled={!isValid}
          isLoading={isSubmitLoading}
          onClick={() => onSubmit(handlingValues)}
          dataTestId="nextButton"
        >
          <SafeFormattedMessageWithoutSpread message={messages.nextButton} />
        </Button>

        <Button
          type={BUTTON_TYPES.BUTTON}
          tertiary
          onClick={() => {
            onPreviousStep({});
          }}
          icon={<ArrowNakedLeftIcon />}
          position={SUPPORTED_ICON_POSITIONS.LEFT}
          dataTestId="previousButton"
        >
          <SafeFormattedMessageWithoutSpread
            message={messages.previousButton}
          />
        </Button>
      </PageFooterContainer>
    </>
  );
};
